import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  "Compenso artista",
  "Service audio & luci",
  "Palco e allestimento",
  "SIAE / diritti",
  "Trasporto e logistica",
  "Backline e strumentazione",
  "Sicurezza e permessi",
  "Effetti speciali",
  "Regia / coordinamento",
];

export default function CostItemsEditor({ request, onUpdated }) {
  const [items, setItems] = useState(request.cost_items || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => setItems(request.cost_items || []), [request.id]);

  const upd = (i, k, v) =>
    setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = (label = "") =>
    setItems([...items, { label, amount: 0 }]);
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));

  const total = items.reduce((s, it) => s + Number(it.amount || 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      const payload = items
        .filter((it) => it.label && it.label.trim())
        .map((it) => ({ label: it.label.trim(), amount: Number(it.amount) || 0 }));
      const { data } = await api.patch(`/quote-requests/${request.id}/cost-items`, {
        items: payload,
      });
      toast.success("Voci salvate");
      onUpdated && onUpdated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 border-t av-hairline pt-4" data-testid={`cost-editor-${request.id}`}>
      <div className="av-eyebrow mb-2">Voci di costo</div>
      <div className="flex flex-wrap gap-1 mb-3">
        {PRESETS.filter((p) => !items.find((it) => it.label === p)).map((p) => (
          <button
            key={p}
            onClick={() => add(p)}
            className="text-[11px] av-pill av-pill-ghost"
            data-testid={`cost-preset-${p.split(" ")[0].toLowerCase()}`}
          >
            + {p}
          </button>
        ))}
        <button
          onClick={() => add("")}
          className="text-[11px] av-pill av-pill-ghost"
          data-testid="cost-add-custom"
        >
          <Plus size={12} /> Voce libera
        </button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={`${it.label || "row"}-${i}`} className="flex gap-2 items-center" data-testid={`cost-row-${i}`}>
            <input
              className="av-input flex-1"
              value={it.label}
              onChange={(e) => upd(i, "label", e.target.value)}
              placeholder="Descrizione"
              data-testid={`cost-label-${i}`}
            />
            <div className="relative w-40">
              <input
                type="number"
                step="0.01"
                className="av-input pr-8 text-right"
                value={it.amount}
                onChange={(e) => upd(i, "amount", e.target.value)}
                data-testid={`cost-amount-${i}`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A39E93] text-xs">€</span>
            </div>
            <button
              onClick={() => remove(i)}
              className="text-[#A39E93] hover:text-red-300 px-2"
              data-testid={`cost-remove-${i}`}
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm">
          <span className="text-[#A39E93]">Totale: </span>
          <span className="font-mono text-[#E5A93C] text-lg" data-testid={`cost-total-${request.id}`}>
            € {total.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="av-pill av-pill-gold text-xs"
          data-testid={`cost-save-${request.id}`}
        >
          <Save size={12} /> {saving ? "Salvo…" : "Salva voci"}
        </button>
      </div>
    </div>
  );
}
